import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppStore } from '../store/useAppStore';
import { useSettingsStore } from '../store/useSettingsStore';
import { isCustomerOrder } from '../utils/orderUtils';
import { containsOutOfStock, suggestAlternatives } from '../utils/stockUtils';
import { vibrate, overdueVibrationPattern } from '../utils/vibration';
import { isAppHidden, isStandalone, notify } from '../utils/pwa';
import { playBell } from '../utils/audio';

// Out-of-stock notices for own queued orders awaiting customer acknowledgment:
// 3 bell strikes + 3x3 vibration when visible (replayed on focus if missed
// while hidden), silent background PWA notification when installed and hidden.
// Fires once per order per condition period — the server clears the flag when
// the customer decides, a fresh stock change re-flags and re-notifies.
export function useStockNotices() {
  const { t } = useTranslation();
  const orders = useAppStore((state) => state.orders);
  const config = useAppStore((state) => state.config);
  const currentCustomer = useSettingsStore((state) => state.currentCustomer);

  const noticeStates = useRef(new Map());
  const missedNotices = useRef(new Set());

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const hidden = isAppHidden();
    const installed = isStandalone();

    const affected = new Map();
    for (const order of orders) {
      const awaiting = order.state === 'queued' && isCustomerOrder(order, currentCustomer) && order.awaitingCustomerAck;
      if (awaiting) {
        affected.set(order.id, containsOutOfStock(order, config));
      }
    }

    // Orders that are no longer awaiting acknowledgment lose their alert state
    // so a later stock change notifies again.
    for (const orderId of [...noticeStates.current.keys()]) {
      if (!affected.has(orderId)) {
        noticeStates.current.delete(orderId);
        missedNotices.current.delete(orderId);
      }
    }

    for (const [orderId, toppings] of affected) {
      if (toppings.length === 0) {
        continue;
      }
      const state = noticeStates.current.get(orderId) || { alerted: false, notified: false };

      if (!state.alerted) {
        state.alerted = true;
        playBell(3);
        if (hidden) {
          missedNotices.current.add(orderId);
        } else {
          vibrate(overdueVibrationPattern(3));
        }
      }

      if (!state.notified && hidden && installed) {
        state.notified = true;
        const order = orders.find((entry) => entry.id === orderId);
        // Rolled once per condition period so the body stays static.
        state.alternatives = state.alternatives
          || suggestAlternatives(toppings[0], config, orderToppings(order));
        const body = state.alternatives.length > 0
          ? t('notif.outOfStockBody', { orderNo: order.orderNo, topping: toppings[0], alternatives: state.alternatives.join(', ') })
          : t('notif.outOfStockNoAlt', { orderNo: order.orderNo, topping: toppings[0] });
        notify(t('stock.noticeTitle'), { body, tag: `stock-${orderId}` });
      }

      noticeStates.current.set(orderId, state);
    }
  }, [orders, config, currentCustomer, t]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const handleVisibility = () => {
      if (document.visibilityState !== 'visible') return;

      for (const orderId of [...missedNotices.current]) {
        const order = orders.find((entry) => entry.id === orderId);
        const stillAffected = !!order
          && order.state === 'queued'
          && isCustomerOrder(order, currentCustomer)
          && order.awaitingCustomerAck
          && containsOutOfStock(order, config).length > 0;
        if (!stillAffected) {
          missedNotices.current.delete(orderId);
          continue;
        }
        playBell(3);
        vibrate(overdueVibrationPattern(3));
        missedNotices.current.delete(orderId);
      }
    };

    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [orders, config, currentCustomer]);
}

function orderToppings(order) {
  return [...(order?.toppings || []), ...(order?.parts || []).flatMap((part) => part.toppings || [])];
}
